import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from '../shadcn-ui/dialog';
import { Loader2, Pencil, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../shadcn-ui/button';
import FormField from '../FormField';
import { deleteMedia, updateSingleLotProcessStep } from '@/network/api.service';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ProcessingStepSchema } from '@/schema/process-lot-schema';
import { CardContent } from '../shadcn-ui/card';
import MediaUploader from './MediaUploader';
import { DialogTitle } from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { useMasterStore } from '@/stores/useMaster.store';
import { Label } from '../shadcn-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../shadcn-ui/select';
import { useMediaUploader } from '@/hooks/useMediaUploader';

const UpdateProcessingStepModal = ({ stepData, lotId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const agentList = useMasterStore(state => state.agentList);
  const [agentSpecificList, setAgentSpecificList] = useState([]);
  const [selectedAgentType, setSelectedAgentType] = useState('');
  const [mediaToRemove, setMediaToRemove] = useState([]);

  const uploadRef = useRef(null);
  const media = useMediaUploader({ folder: 'lot-process-media' });

  const agentTypeOptions = agentList
    ? Object.keys(agentList).map(key => ({
        label: key,
        value: key,
      }))
    : [];

  const {
    control,
    register,
    reset,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(ProcessingStepSchema),
  });

  const { fields, remove } = useFieldArray({
    control,
    name: 'data.media',
  });

  useEffect(() => {
    if (!stepData) return;

    // setPageLoading(true);
    reset({
      sequence: stepData.sequence,
      name: stepData.name,
      status: stepData.status ?? '',
      data: {
        input_weight_kg: stepData?.data?.input_weight_kg ?? '',
        start_at: stepData?.data?.start_at ?? new Date(),
        ...(stepData?.name !== 'Fermentation' && { end_at: stepData?.data?.end_at ?? new Date() }),
        ...stepData?.data,
      },
    });
    // setValue('data.input_weight_kg', lot?.weight_of_coffee ?? '');

    // Initialize selectedAgentType and populate agentSpecificList when agent_type exists
    if (stepData?.data?.agent_type) {
      setSelectedAgentType(stepData.data.agent_type);

      // Populate agentSpecificList immediately if agentList is available
      if (agentList && agentList[stepData.data.agent_type]) {
        const specifics = agentList[stepData.data.agent_type].map(agent => ({
          label: agent.agent_specifics,
          value: agent.agent_specifics,
          code: agent.code,
        }));
        setAgentSpecificList(specifics);
      }
    }
  }, [stepData, reset, setValue, agentList]);

  const onSaveDetails = async data => {
    setLoading(true);
    try {
      if (mediaToRemove.length > 0) {
        await deleteMedia(mediaToRemove.map(m => m.path));
        setMediaToRemove([]);
      }

      if (uploadRef.current) {
        await uploadRef.current();
      }
      const uploaded = await media.uploadAll();
      const existingMedia = Array.isArray(data.media) ? data.media : [];

      const mergedMedia = uploaded && uploaded.length > 0 ? [...existingMedia, ...uploaded] : existingMedia;

      // Remove process_checks from data if it exists
      const { process_checks, ...dataWithoutChecks } = data?.data || {};
      const filteredData = {
        ...data,
        data: { ...dataWithoutChecks, media: mergedMedia },
      };

      const respData = await updateSingleLotProcessStep({
        data: filteredData,
        lot_id: lotId,
        process_step_id: stepData?._id,
      });

      if (respData?.data?.success) {
        toast.success('Details saved successfully.');
        setOpen(false);
        media.setLocalMedia([]);
        media.setUploadedMedia([]);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || 'Failed to save details');
    } finally {
      setLoading(false);
    }
  };

  const onRemoveMedia = file => {
    setMediaToRemove(prev => [...prev, file]);
    const index = fields.findIndex(m => m._id === file._id);

    if (index !== -1) {
      remove(index);
    }
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    console.log('Form submit triggered');
    console.log('Form errors:', errors);
    handleSubmit(onSaveDetails)(e);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className="rounded-full p-1 hover:bg-muted" aria-label="Edit lot information">
          <Pencil className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogHeader className="hidden">
        <DialogTitle>Stage Data</DialogTitle>
      </DialogHeader>
      <DialogContent
        className={cn(
          'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-w-md rounded-2xl p-0 [&>button]:hidden',
          'max-h-[90vh] overflow-y-auto'
        )}
      >
        <DialogTitle className="p-4 pb-0 font-medium">Stage Data</DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <CardContent className="flex flex-col gap-3 p-4 pt-0">
            {/* Fermentation Only Fields */}
            {stepData?.name === 'Fermentation' && (
              <div className="mb-1 flex flex-col gap-1">
                <FormField
                  select
                  label="Aerobic"
                  name="data.aerobic"
                  control={control}
                  errors={errors}
                  required
                  placeholder="Select type"
                  disabled={loading}
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                />

                <FormField
                  select
                  label="Modality"
                  name="data.modality"
                  control={control}
                  errors={errors}
                  required
                  placeholder="Select modality"
                  disabled={loading}
                  options={[
                    { label: 'Fermentation tank', value: 'Fermentation tank' },
                    { label: 'Closed plastic drum', value: 'Closed plastic drum' },
                    { label: 'ecotact bags', value: 'ecotact bags' },
                    { label: 'Open tank', value: 'Open tank' },
                  ]}
                />

                <Controller
                  name="data.agent_type"
                  control={control}
                  rules={{ required: 'Agent type is required' }}
                  render={({ field, fieldState }) => {
                    // Get current agent_specific value
                    const currentAgentSpecific = control._formValues?.data?.agent_specific;

                    return (
                      <div className="space-y-1.5">
                        <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                          Agent type<span className="">*</span>
                        </Label>
                        <Select
                          value={field.value || ''}
                          onValueChange={value => {
                            field.onChange(value);
                            setSelectedAgentType(value);
                            // Only reset agent specific if it's not already present
                            if (!currentAgentSpecific) {
                              setValue('data.agent_specific', '');
                            }
                          }}
                          disabled={loading}
                        >
                          <SelectTrigger
                            className={cn('rounded-[10px] bg-white', fieldState.error && 'border-red-500')}
                          >
                            <SelectValue placeholder="Select agent type" />
                          </SelectTrigger>
                          <SelectContent>
                            {agentTypeOptions.map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fieldState.error && <div className="text-xs text-red-500">{fieldState.error.message}</div>}
                      </div>
                    );
                  }}
                />

                <FormField
                  select
                  label="Agent specific"
                  name="data.agent_specific"
                  control={control}
                  errors={errors}
                  required
                  placeholder="Select agent specific"
                  disabled={loading || !selectedAgentType}
                  options={agentSpecificList}
                />

                <FormField
                  textarea
                  name="data.notes"
                  label={'Notes on fermentation agent'}
                  placeholder={'Type specifics'}
                  disabled={loading}
                  required
                  errors={errors}
                  register={register}
                />

                <FormField
                  textarea
                  name="data.recipe"
                  label={'Recipe'}
                  placeholder={'Add recipe'}
                  disabled={loading}
                  required
                  errors={errors}
                  register={register}
                />
              </div>
            )}

            {/* Available for all stages */}
            {/* Start At */}
            <FormField
              datetime
              name="data.start_at"
              control={control}
              disabled={loading}
              errors={errors}
              required
              label={
                stepData?.name === 'Fermentation'
                  ? 'Fermentation start time'
                  : `Start of ${stepData?.name.toLowerCase()}`
              }
            />

            {stepData?.name === 'Fermentation' && stepData?.status === 'Completed' && (
              <div className="flex flex-col gap-3">
                {/* Start At */}
                <FormField
                  datetime
                  name="data.end_at"
                  control={control}
                  disabled={loading}
                  errors={errors}
                  required
                  label={
                    stepData?.name === 'Fermentation'
                      ? 'Fermentation end time'
                      : `end of ${stepData?.name.toLowerCase()}`
                  }
                />

                {/* Output weight of coffee */}
                <FormField
                  name="data.output_weight_kg"
                  label="Output weight of coffee"
                  required
                  type="number"
                  inputMode="decimal"
                  placeholder="Enter weight"
                  step="0.01"
                  disabled={loading}
                  register={register}
                  errors={errors}
                />
              </div>
            )}

            {stepData?.name !== 'Fermentation' && (
              <div className="flex flex-col gap-3">
                {/* Input Weight of coffee */}
                <FormField
                  label="Input weight of coffee (Kg)"
                  name="data.input_weight_kg"
                  required
                  type="number"
                  inputMode="decimal"
                  placeholder="Enter weight"
                  step="0.01"
                  disabled={loading}
                  register={register}
                  errors={errors}
                />
                {/* {errors?.data?.input_weight_kg && (
                    <div className="text-xs text-red-500">{errors.data.input_weight_kg.message}</div>
                  )} */}

                {/* Output Weight of coffee */}
                <FormField
                  name="data.output_weight_kg"
                  label="Output weight of coffee"
                  required
                  type="number"
                  inputMode="decimal"
                  placeholder="Enter weight"
                  step="0.01"
                  disabled={loading}
                  register={register}
                  errors={errors}
                />

                {/* Weight of skin */}
                {stepData?.name === 'Pulping' && (
                  <FormField
                    label="Weight of skin"
                    name="data.weight_of_skin_kg"
                    required
                    type="number"
                    inputMode="decimal"
                    placeholder="Enter weight"
                    step="0.01"
                    disabled={loading}
                    register={register}
                    errors={errors}
                  />
                )}

                {/* End At */}
                <FormField
                  datetime
                  name="data.end_at"
                  disabled={loading}
                  control={control}
                  errors={errors}
                  required
                  label={`End of ${stepData?.name.toLowerCase()}`}
                />
              </div>
            )}

            {/* Media Inputs */}
            <MediaUploader
              media={media}
              label="Photos/Videos"
              folder={'lot-process-media'}
              onUploaded={fn => (uploadRef.current = fn)}
              className="bg-transparent"
              loading={loading}
            >
              {fields?.map((m, idx) => (
                <div key={idx} className="relative h-[72px] w-[72px] overflow-hidden rounded-xl">
                  {m.mime_type === 'image' ? (
                    <img src={m.public_url} className="h-full w-full object-cover" />
                  ) : (
                    <video src={m.public_url} className="h-full w-full object-cover" muted playsInline />
                  )}

                  <button
                    type="button"
                    onClick={() => onRemoveMedia(m)}
                    className="absolute right-1 top-1 rounded-full bg-white/80 p-0.5"
                    disabled={loading}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </MediaUploader>

            <div className="mt-4 flex flex-col gap-2">
              <Button
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (media.localMedia.length > 0) {
                    media.setLocalMedia([]);
                    media.setUploadedMedia([]);
                  }
                }}
                disabled={loading}
              >
                close
              </Button>

              <Button
                type="submit"
                disabled={loading}
                // onClick={() => {
                //   console.log('Button clicked');
                //   console.log('Form errors:', errors);
                // }}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spinSlow" />
                    Saving...
                  </>
                ) : (
                  'Save details'
                )}
              </Button>
            </div>
          </CardContent>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateProcessingStepModal;
